import { useCallback, useMemo, useState } from 'react';
import {
  shouldShowInquiryStepper,
  STEP_TRIP_DETAILS,
  STEP_YOUR_ACCOUNT,
} from '../packageInquirySteps';
import { getInquiryTripDetailsErrors } from '../inquiryPackageModel';

const TOUCH_ON_CONTINUE = ['partySize', 'message'];

const useInquiryPackageActions = ({
  steps,
  step,
  next,
  listing,
  selectedPackage,
  params,
  currentUser,
  followSubjectMutation,
  invalid,
  disabled,
  startDate,
  endDate,
  message,
  partySize,
  form,
}) => {
  const [showValidation, setShowValidation] = useState(false);
  const isTripDetails = step?.id === STEP_TRIP_DETAILS.id;
  const isAccountStep = step?.id === STEP_YOUR_ACCOUNT.id;

  const tripStep = useMemo(() => steps.find(item => item.id === STEP_TRIP_DETAILS.id) || null, [
    steps,
  ]);

  const followProperty = useCallback(() => {
    const listingId = listing?.id?.uuid;
    const userId = currentUser?.id?.uuid;
    if (userId && listingId) {
      followSubjectMutation(userId, listingId, { sms: true, email: true });
    }
  }, [currentUser, followSubjectMutation, listing]);

  const missing = useMemo(
    () => getInquiryTripDetailsErrors({ startDate, endDate, message, partySize }),
    [startDate, endDate, message, partySize]
  );
  const hasMissing = Object.keys(missing).length > 0;

  // Returns true when the trip details can move on. Otherwise marks the fields touched so their
  // errors render (the button used to be disabled, which failed silently on mobile).
  const validateTripDetails = useCallback(() => {
    if (!hasMissing && !invalid) {
      return true;
    }
    setShowValidation(true);
    if (form) {
      const registered = form.getRegisteredFields ? form.getRegisteredFields() : [];
      TOUCH_ON_CONTINUE.filter(name => registered.includes(name)).forEach(name => {
        form.focus(name);
        form.blur(name);
      });
    }
    return false;
  }, [form, hasMissing, invalid]);

  const onContinue = useCallback(() => {
    if (!validateTripDetails()) {
      return;
    }
    next();
  }, [next, validateTripDetails]);

  const stepperSteps = useMemo(
    () =>
      steps
        .filter(item => item.id)
        .map(item => ({
          id: item.id,
          label: item.label,
        })),
    [steps]
  );

  const packageParams = useMemo(
    () => ({
      id: listing?.id?.uuid || params.id,
      slug: params.slug,
      packageId: selectedPackage?.id,
    }),
    [listing, params, selectedPackage]
  );

  return {
    isTripDetails,
    isAccountStep,
    tripStep,
    followProperty,
    onContinue,
    submitDisabled: Boolean(disabled),
    validateTripDetails,
    validationErrors: showValidation ? missing : {},
    showValidationSummary: showValidation && (hasMissing || Boolean(invalid)),
    stepperSteps,
    showStepper: shouldShowInquiryStepper(stepperSteps),
    packageParams,
  };
};

export default useInquiryPackageActions;
